import React, { useState } from "react";
import "../assets/css/ReleaseNotes.css";

const releaseData = {
  2026: {
    September: [
      {
        title: "What's New",
      },
      {
        title: "Restore Report Redesign (Major Release)",
        description:
          "The restore report has been redesigned to show the outcome of a restore at a glance.",
        subpoints: [
          "New summary card with result counts. Click a count to filter the report.",
          "Collapsible network and device sections, shown by name.",
          "Clearer, more accurate result for each setting.",
        ],
      },
      {
        title: "Restore Report Comparison Views",
        description:
          "Compare each setting's backup and pre-restore configuration against the current configuration, with unchanged lines collapsed so differences stand out.",
      },
      {
        title: "Expanded API Coverage",
        description:
          "Backups now include additional Dashboard API endpoints, including organization-wide firewall and group policies, SASE, Campus Gateway, cellular data profiles and wireless SSID profiles.",
      },
      {
        title: "Clearer Backup Failure Reasons",
        description:
          "Backups that fail due to an invalid API key or IP restrictions now show the reason on the info icon in Backup History.",
      },
      {
        title: "Restore Accuracy Improvements",
        subpoints: [
          "Fixed a duplicate Default rule when restoring firewall rules",
          "Fixed restoring renamed group policies",
          "Fixed Wireless RF profile restore",
          "Improved Wireless SSID restore",
        ],
      },
    ],
    August: [
      {
        title: "What's New",
      },
      {
        title: "Multiple Device & Network Restore (Major Release)",
        description:
          "Added the ability to restore multiple devices or multiple networks in a single restore.",
      },
      {
        title: "Partially Successful Backups",
        description:
          "If a backup is unable to finish due to reasons such as external traffic, it exits with a PARTIALLY SUCCESSFUL state with whatever it has backed up. These can be downloaded and restored from as well.",
      },
      {
        title: "Backup Status Insights",
        description:
          "In the event of a PARTIALLY SUCCESSFUL or FAILED backup, hover on the info icon to see the reason for failure.",
      },
      {
        title: "Rate Limit Optimisation",
        description:
          "Improved how backups handle Meraki API rate limits, making backups more resilient for organizations with heavy API usage.",
      },
      {
        title: "Organization Sync Improvements",
        description:
          "Added the ability to sync organisation name changes, plus reliability improvements.",
      },
      {
        title: "Restore Accuracy Improvements",
        subpoints: [
          "Fixed OSPF routing configuration restore",
          "Fixed per-port VLAN settings restore on switches",
          "Fixed Content Filtering settings restore",
          "Fixed Wireless SSID Layer 3 firewall rules and splash page settings restore",
          "Fixed false mismatches caused by firmware version metadata during restore comparisons",
        ],
      },
    ],
    July: [
      {
        title: "What's New",
      },
      {
        title: "Device Restore (Major Release)",
        description:
          "Added support for restoring individual devices, allowing selected device configurations to be restored without restoring the entire network.",
      },
      {
        title: "License Management (Major Release)",
        description:
          "Added a centralized view for managing purchased licenses, making it easier to view account licenses and assign them to organizations.",
      },
      {
        title: "Network Count in Backup History",
        description:
          "The Backup History page now displays the number of networks included in each backup, making it easier to track network counts over time and stay aligned with license entitlements.",
      },
      {
        title: "Bug Fixes",
        subpoints: [
          "Fixed an issue restoring the default Layer 3 firewall rule for Wireless (MR) networks.",
        ],
      },
    ],
    June: [
      {
        title: "In-App Support Portal",
        description:
          "Introduced a new support portal, available directly from the BackupMeraki site for contacting support or raising a ticket.",
      },
    ],
    May: [
      {
        title: "What's New",
      },
      {
        title: "Static IP Address for Backup & Restore",
        description:
          "Organizations that restrict Dashboard and API access by IP can now request a dedicated static IP address for their allow list.",
      },
      {
        title: "Faster Network List Loading for Restore",
        description:
          "Improved network list loading during network-level restore, so all networks now populate quickly and reliably even for large organizations.",
      },
    ],
    January: [
      {
        title: "What’s New",
      },
      {
        title: "Rate Limit Error Tracking",
        description:
          "Improved handling of API rate limit (HTTP 429) responses to reduce noisy failures and improve overall backup stability.",
      },
      {
        title: "Backup Operation Tracking",
        subpoints: [
          "Enhanced status tracking and reporting for backup operations.",
          "Improved log and output readability for easier troubleshooting.",
        ],
      },
      {
        title: "Maintenance Updates",
        description:
          "Updated backup and restore processors to include the latest data from API documentation and specifications.",
      },
    ],
  },
  2025: {
    December: [
      {
        title: "What’s New",
      },
      {
        title: "Expanded Switch Routing Backup Coverage",
        description:
          "Added support for backing up additional Layer 3 routing configuration for Meraki switches and switch stacks, including:",
        subpoints: [
          "Switch stack SVI (Switched Virtual Interface) configuration",
          "DHCP settings associated with switch stack SVIs",
          "Static routes configured on switch stacks",
        ],
      },
      {
        title: "Physical Switch SVI Enhancements",
        description:
          "Added backup support for DHCP configuration on SVIs configured directly on physical switches (non-stack scenarios).",
      },
    ],
    September: [
      {
        title: "What’s New",
      },
      {
        title: "New Device Support",
        description:
          "Added device support for Cisco Catalyst CW-, C9200, C9300, and C9500 series.",
      },
      {
        title: "Expanded API Coverage",
        description:
          "BackupMeraki now supports additional Cisco Meraki Dashboard API endpoints, including:",
        subpoints: [
          "DNS Local Profiles (Appliance)",
          "DNS Split Profiles (Appliance)",
          "Multicast Forwarding (Appliance)",
          "XDR Integrations",
          "DeviceController Migrations",
          "RADSEC Certificates (Wireless)",
          "Location Scanning (Wireless)",
          "SSID Firewall Allowlist (Wireless)",
          "Device-Level Settings (Appliance Radio Settings, Appliance Uplink Settings, Cellular SIM Configuration, Sensor Relationships, Wireless Electronic Shelf Labels)",
          "Network-Level Settings (Inbound Cellular Firewall Rules, Appliance Firewall Settings, Delegated Prefixes, Appliance SSIDs, RF Profiles, VLAN Profiles, Wireless Ethernet Port Profiles, Switch DHCP ARP Inspection Trusted Servers, Wireless Electronic Shelf Label Profiles)"
        ],
      },
      {
        title: "UI Enhancements",
        subpoints: [
          "Improved date and time formatting on the Backup History page.",
          "Added a new Duration column for better visibility of backup execution time.",
          "Refined placeholder visuals on the Registration page for a cleaner appearance.",
        ],
      },
      {
        title: "Bug Fixes",
        subpoints: [
          "Resolved an issue affecting the restoration of certain MX VLAN parameters.",
        ],
      },
    ],    
    August: [
      {
        title: "Regional Dashboard Support",
        description:
          "Users can now select a preferred Cisco Meraki Dashboard region:",
        subpoints: [
          "Global (api.meraki.com)",
          "Canada (api.meraki.ca)",
          "India (api.meraki.in)",
          "Government (api.gov-meraki.com)",
          "China (api.meraki.cn)",
        ],
      },
      {
        title: "MFA security notification",
        description:
          "Added a notification encouraging users to enable Multi-Factor Authentication (MFA) for stronger account security.",
      },
      {
        title: "Restore report enhancements",
        
        subpoints: [
          "Git-style diffs to highlight changes between backup and live configuration.",
          "Updated status icons for clearer match/mismatch indicators.",
        ],
      },
      {
        title: "Backup engine updates",
        description: "Added support for CW9K Access Points.",
      },
      {
        title: "Restore history",
        description:
          "New Started By field to identify the user who initiated a restore.",
      },
    ],
    July: [
      {
        title: "Multi-user Workspaces (Multi-admin Support)",
        description:
          "Introduced support for shared access to a single BackupMeraki Workspace. Now, multiple users can manage backups, restores, and API key management within the same environment. This feature is especially valuable for:",
        subpoints: [
          "MSPs managing multiple clients or teams",
          "Enterprise IT departments with multiple administrators",
        ],
        extra:
          "Admins can now invite others to join their workspace, enabling secure collaboration and efficient role delegation within a controlled access environment.",
      },
      {
        title: "New Pricing Plans",
        description:
          "Introduced scalable pricing plans tailored to better match different organization sizes and needs.",
      },
      {
        title: "Retired Free Plan",
        description:
          "We have officially decommissioned the Free plan to focus on delivering a high-quality Premium experience, including enhanced support, reliability, and feature enhancements.",
      },
    ],
    June: [
      {
        title:"What's New"
      },
      {
        title: "UI and Performance Improvements",
        subpoints: [
          "Numerous interface adjustments for a more seamless and intuitive experience.",
          "Improved loading performance and responsiveness of the “General Settings” page.",
          "Refined dropdown menu behavior on the Organizations page for better usability.",
          "Fixed minor display issues for the API settings page.",
        ],
      },
      {
        title: "Security Enhancements",
        description:
          "Internal logging has been hardened by minimizing console outputs and aligning with modern security and privacy standards.",
      },
    ],
    May: [
      {
        title: "Major Update",
        subpoints: [
          "New Network Type Restore Support",
          "MT (Sensors) – Configuration backup and restore support for MT environmental sensors.",
          "MG (Cellular Gateway) – Added restore capabilities for MG cellular network configurations.",
          "MV (Cameras) – Enabled restore support for MV smart camera settings and configurations.",
        ],
      },
    ],
    April: [
      {
        title: "Major Update",
        subpoints: [
          "New Network Type Restore Support",
          "MR (Wireless) – Comprehensive restore support for Meraki MR Wireless networks.",
        ],
      },
      {
        title: "Restored Wireless Settings include",
        subpoints: [
          "General Device Configuration",
          "Alternate Management Interface",
          "IoT Radio Settings",
          "RF Profiles",
          "Billing Settings",
          "SSIDs",
          "Layer 3 Firewall Rules",
          "Layer 7 Firewall Rules",
          "Splash Page Settings",
          "Traffic Shaping Rules",
          "Identity PSKs",
          "Bonjour Forwarding",
          "Group Policies by Device Type",
          "EAP Overrides",
          "Hotspot 2.0",
          "SSID Availability Schedules",
          "VPN Tunneling",
        ],
      },
    ],
    March: [
      {
        title: "Major Update",
        subpoints: ["Network Type Restore support released"],
      },
      {
        title: "Supported Network types",
        subpoints: ["MX Security Appliance", "MS Switches"],
      },
      {
        title: "General Network Settings",
        subpoints: [
          "General Configuration",
          "Firmware Upgrades",
          "Group Policies",
          "NetFlow",
          "Device Configuration",
          "SNMP",
          "Logging",
          "Traffic Analysis",
          "Webhooks",
          "Alerts Settings",
        ],
      },
      {
        title: "MX Security Appliance Settings",
        subpoints: [
          "Warm Spare Configuration",
          "Addressing & VLANs",
          "Single LAN & Per-port VLAN Settings",
          "VLAN Routing",
          "Site-to-site VPN",
          "BGP Routing",
          "Uplink Statistics",
          "Content Filtering",
          "SD-WAN & Traffic Shaping",
          "Security",
          "Firewall Rules",
          "NAT Rules",
        ],
      },
      {
        title: "MS Switch Settings",
        subpoints: [
          "VLAN Configuration",
          "Alternate Management Interface",
          "STP, Storm Control, MTU",
          "QoS Rules",
          "Multicast & Rendezvous Points",
          "OSPF Routing",
          "Port Schedules",
          "ACLs",
          "Access Policies",
          "DHCP Server Policy",
          "DSCP to CoS Mappings",
          "Link Aggregations",
          "Switch Stacks",
        ],
      },
    ],
    February: [
      {
        title: "What's New",
      },
      {
        title: "Added support for new Meraki API calls, extending coverage to",
        subpoints: [
          "Traffic shaping VPN exclusions",
          "Camera boundaries and roles",
          "Cellular gateway eSIM inventory & providers",
          "Early access features",
          "Policy objects & object groups",
          "Sentry policy assignments",
          "Custom splash themes",
          "Wireless Air Marshal rules & settings",
          "RF profile assignments",
        ],
      },
    ],
    January: [
      {
        title: "What's New",
        subpoints: [
          "Beta Release: Organization-Wide Restore",
          "Launched the initial beta version of the organization-wide restore feature.",
        ],
      },
      {
        title: "Initial Restore Coverage",
        subpoints: [
          "SNMP Settings",
          "Policy Objects",
          "Policy Object Groups",
          "Intrusion Detection Allow List",
          "Third-Party VPN Peers",
          "SAML Settings",
          "SAML Identity Providers (IdPs)",
          "SAML Roles",
          "Alert Profiles",
          "Early Access Feature Opt-Ins",
          "VPN Firewall Rules",
        ],
      },
    ],
  },
  2024: {
    November: [
      {
        title: "What’s New",
        subpoints: [
          {
            title: "Dependency Upgrades",
            description:
              "Eliminated vulnerabilities flagged by security scanners.",
          },
        ],
      },
      {
        subpoints: [],
      },
    ],
    August: [
      {
        title: "What’s New",
        subpoints: [
          {
            title: "Backup Engine Upgrade",
            description: "Backups now 25% faster.",
          },
          {
            title: "Partial API Failure Handling",
            description: "Improved resiliency.",
          },
          {
            title: "JSON Bug Workaround",
            description:
              "Ensured compatibility with malformed Meraki responses.",
          },
          {
            title: "Telemetry Expansion",
            description: "Improved insight into performance bottlenecks.",
          },
        ],
      },
    ],
    June: [
      {
        title: "What’s New",
      },
      {
        title:"Global Region Selection :",
        description:"Choose data storage regions across 11 global locations:",
        subpoints:[
          "Africa – Cape Town",
          "Asia Pacific – Tokyo, Singapore",
          "Australia – Sydney",
          "Canada – Central",
          "Europe – Frankfurt, Ireland",
          "Middle East – UAE",
          "South America – São Paulo",
          "United States – East (N. Virginia), West (N. California)"
        ]
      },
      {
        title:"Policy Update",
        description:"Updated data residency policy for compliance.",
      },
      {
        title:"Security Review",
        description:"Closed gaps and reinforced encryption."
      }

    ],
    May: [
      {
        title: "What’s New",
        subpoints: [
          {
            title: "Cisco Meraki Bug Workaround",
            description:
              "Ensured accurate data retrieval for getNetworkApplianceVpnBgp.",
          },
          {
            title: "Backup Engine Tuning",
            description: " Enhanced performance under high load.",
          },
        ],
      },
    ],
    April: [
      { title: "Major"},
      {
        
        title: "Premium Version Launch What’s New:",
        subpoints: [
          "Secure API Key Storage",
          "Flexible Automatic Backup Scheduling",
          "Permanent Cloud Storage with regional options",
          "On-Demand Backup",
          "Custom Timezone Support",
          "Payments via Stripe, enabling self-service plan management.",
          "Full-featured User Portal",
          "Backend optimization for large-scale organizations",
          "Enhanced real-time error logging",
        ],
      },
    ],
    February: [
      {
        title: "What’s New",
        subpoints: [
          {
            title: "Scheduled Backups (Beta)",
            description: "Automate backups daily, weekly, or monthly.",
          },
          {
            title: "Scheduler Resiliency",
            description:
              "Added failover and retry logic for more reliable execution.",
          },
        ],
      },
    ],
    January: [
      {
        title: "What’s New",
        subpoints: [
          {
            title: "API Key Management Portal",
            description:
              "Delivered the highly requested API Key Management Portal, allowing users to securely store and manage their Meraki API keys within BackupMeraki. Previously, API keys were not stored and users had to provide them manually for every backup session. End-to-End Encryption: Applied to stored API keys.",
          },
          {
            title:"End-to-End Encryption",
            description:"Applied to stored API keys."
          },
          {
            title:"Organization Sync (Beta)",
            description:"Link Meraki organizations and keep them in sync automatically."
          },
          {
            title:"Backend Upgrade",
            description:" Improved core framework for better performance and scalability."
          }
        ],
      },
    ],
  },
  2023: {
    November:[
      {
        title:"What’s New",
        subpoints:[
          {title:"Cisco Meraki Marketplace Verification",description:"Successfully passed the Cisco Meraki Marketplace security review and became an officially verified application."},
          {
            title:"Security Refresh",
            description:"Rolled out a comprehensive upgrade to meet Marketplace standards, including deeper audit logging, enhanced API tracing, and expanded access controls."
          }
        ]
      }
    ],
    June :[
      {
        title:"What’s New",
      },
      {
          title:"Security Enhancements",
          subpoints:[
           "Replaced legacy encryption algorithms with modern, standards-based encryption at rest and in transit",
           "Stricter input validation to prevent malformed requests",
           "Hardened rate limiting to defend against abuse"
        ],
          
      },
      {
        title:"API Coverage Expansion",
        description:"Included newer Meraki features in backups."
      },
      {
        title:"Infrastructure Right-Sizing:",
        description:"Tuned the platform for better performance and cost-efficiency."
      }

      
    ],
    February:[
      {
        title:"What’s New",
        subpoints:[
          {title:"Architectural Redesign",description:"Overhauled the backend to eliminate previous limitations on organization size. Removed the earlier constraint on the number of networks per organization, allowing users to back up organizations of unlimited size."}
        ]
      }
    ]
  },
  2022: {
    December:[
      {
        title:"What’s New",
        subpoints:[
          {title:"Secure Authentication",description:"Introduced mandatory user login before accessing backups, strengthening platform security."},
          {title:"Security Hardening",description:"Applied additional security measures across the backup pipeline and data storage layers."}
        ]
      }
    ],
    August : [
      {
        title:"What’s New",
        subpoints:[
          {title:"Expanded Backup Scope",description:"Added support for backing up organization-level settings, network-level configurations, and device-level parameters."},
          {title:"Smarter Error Handling",description:" Implemented intelligent API error recovery to reduce manual intervention during minor Meraki API issues."}
        ]
      }
    ],
    June : [
      {
        title:"What’s New",
        subpoints:[
          {title:"Website Revamp",description:"Launched a redesigned website with a clearer product overview, improved FAQ section, and expanded security documentation."},
          {title:"Proactive Monitoring",description:"Enhanced internal systems to automatically detect abnormal backup performance and errors."}
        ]
      }
    ],
    May:[
      {
        title:"What’s New",
        subpoints:[
          {title:"Updated Policies",description:"Revised our Privacy Policy and Terms & Conditions to align with evolving global data privacy regulations and maintain transparency."}
        ]
      }
    ],
    February : [
      {
        title:"What’s New",
        subpoints:[
          {
            title:"User Analytics Dashboard",
            description:"Introduced analytics to help us better understand how users interact with BackupMeraki, enabling data-driven product improvements."
          },
          {title:"Performance Boost",description:"Optimized backend processing, reducing average backup times by over 30%."}
        ]
      }
    ]
  },
  2021: {
    May:[
      {title:"What’s New",
        subpoints:[
          {title:"Official launch",description:"We introduced BackupMeraki Online, our Minimum Viable Product (MVP), designed to give early adopters a hands-on backup experience and help us gather crucial feedback from the community."},
          {title:"Free online backup form",description:"Offered an easy entry point for small organizations to back up their Cisco Meraki configurations."},
          {title:"Cloud-Native Foundation",description:"Built on a scalable cloud infrastructure from day one to ensure long-term growth and future-proofing."}
        ]
      }
    ]
  },
};

const ReleaseNotes = () => {
  const [selectedYear, setSelectedYear] = useState(null); 
  const [selectedMonth, setSelectedMonth] = useState(null); 
  const [openYear, setOpenYear] = useState(null);

  const toggleYear = (year) => {
    setOpenYear(openYear === year ? null : year);
  };

  return (
    <>
    <h2 className="main-rel-headig-top">Release Notes</h2>
    <div className="release-container">
    
      {/* Sidebar left side  */}
      <div className="sidebar">
        <h4 className="slide-bar-con-heading">Release Notes</h4>
        {Object.keys(releaseData)
          .sort((a, b) => b - a)
          .map((year) => (
            <div key={year}>
              <button
                className={`year-btn ${selectedYear === year ? "active" : ""}`}
                onClick={() => {
                  setSelectedYear(year);
                  toggleYear(year);
                  setSelectedMonth(null); 
                }}
              >
                {year}
              </button>
              {openYear === year && (
                <div className="months">
                  {Object.keys(releaseData[year]).length > 0 ? (
                    Object.keys(releaseData[year]).map((month) => (
                      <button
                        key={month}
                        className={`month-btn ${
                          selectedYear === year && selectedMonth === month
                            ? "active"
                            : ""
                        }`}
                        onClick={() => {
                          setSelectedYear(year);
                          setSelectedMonth(month);
                        }}
                      >
                        {month}
                      </button>
                    ))
                  ) : (
                    <p className="no-data">No releases</p>
                  )}
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Content */}
      <div className="content">
        {!selectedYear || !selectedMonth ? (
          <>
          
          <p  className="para-for-emp">Select a month to view release notes</p>
          </>
        ) : (
          <>
            <h4 className="content-header">
              {selectedMonth} {selectedYear}
            </h4>
            {releaseData[selectedYear][selectedMonth] ? (
              releaseData[selectedYear][selectedMonth].map((item, idx) => (
                <div key={idx} style={{ marginBottom: "20px" }}>
                  {item.title && (
                    <p style={{ fontWeight: "bold", marginBottom: "8px" }}>
                      {item.title}
                    </p>
                  )}
                  {item.description && <p>{item.description}</p>}
                  {item.subpoints && (
                    <ul style={{ marginLeft: "20px", marginTop: "5px" }}>
                      {item.subpoints.map((point, i) => (
                        <li key={i}>
                          {typeof point === "string" ? (
                            point
                          ) : (
                            <>
                              <strong>{point.title}:</strong>{" "}
                              {point.description}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {item.extra && <p>{item.extra}</p>}
                </div>
              ))
            ) : (
              <p>No release notes available for this year/month.</p>
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default ReleaseNotes;


